/**
 * core/DataMap
 *
 * A class for setting up and communicating with a Leaflet map.
 */

import { noop } from './utils';


export default class DataMap {

    constructor(target, {
        attribution,
        bounds,
        center,
        marker,
        maxZoom,
        minZoom,
        mobile,
        tiles,
        zoom
    }) {

        const mapOptions = {
            fadeAnimation: false,
            //gestureHandling: true,
            maxBounds: bounds || undefined,
            maxZoom,
            minZoom,
        };

        this.DOMNode = L.map(target, mapOptions)
                        .setView(center, zoom);

        this.basemap = L.tileLayer(tiles, { attribution })
                        .addTo(this.DOMNode);

        if (marker && typeof marker === 'object') {
            this.marker = L.circleMarker(center, { ...marker, clickable: false })
                           .setRadius(marker.radius)
                           .addTo(this.DOMNode);

            this.mobile = mobile;

            this.__allowEvents();
        }
    }


    __allowEvents() {
        this.update = noop;
        this.captureMouse = true;

        this._eventsAllowed = true;
        this._attachedEventHandlers = [];

        this.eventHandlers = {
            mousedown: () => {
                this.captureMouse = false;
            },
            mousemove: e => {
                if (this.captureMouse) {
                    this.marker.setLatLng(e.latlng);
                    this.update();
                }
            },
            mouseover: () => {
                this.mobile = false
                this._attachEventHandlers();
            },
            mouseup: () => {
                this.captureMouse = true;
            },
            move: () => {
                this.marker.setLatLng(this.DOMNode.getCenter());
                this.update();
            },
            touchstart: () => {
                this.mobile = true;
                this._attachEventHandlers();
            },
        };

        this._attachEventHandlers();
    }


    /**
     * @param String name
     * @param DOMElement target
     */
    _attachEventHandler(name, target = null) {
        if (target) {
            target.addEventListener(name, this._lookupEventHandler(name));
        }
        else {
            target = this.DOMNode;
            target.on(name, this._lookupEventHandler(name))
        }

        this._attachedEventHandlers.push({
            name,
            target,
        });
    }


    _attachEventHandlers() {
        this._detachEventHandlers();

        if (this.mobile) {
            this._attachEventHandler('move');

            // break from mobile/touch mode
            this._attachEventHandler('mouseover', window);
        }
        else {
            this._attachEventHandler('mouseup')
            this._attachEventHandler('mousedown');
            this._attachEventHandler('mousemove');

            // break from desktop/mouse mode
            this._attachEventHandler('touchstart', window);
        }
    }


    _detachEventHandlers() {
        this._attachedEventHandlers.forEach(({ target, name }) => {
            if (target !== this.DOMNode) {
                target.removeEventListener(name, this._lookupEventHandler(name));
            }
            else {
                target.off(name, this._lookupEventHandler(name));
            }
        });

        this._attachedEventHandlers = [];
    }


    /**
     * @param String name
     * @return Function
     */
    _lookupEventHandler(name) {
        return this.eventHandlers[name] || noop;
    }


    /**
     * @param LeafletLayer layer
     */
    addLayer(layer) {
        layer.addTo(this.DOMNode);
    }


    /**
     * @param Function listener
     */
    setEventListener(listener) {
        if (this._eventsAllowed) {
            this.update = listener;
        }
        else {
            throw new Error('You must specify marker options in your map config to listen for map events');
        }
    }

}
